import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
	MotornetVehicleImportValidateRequestItem,
	MotornetVehicleImportValidationMatch,
	MotornetVehicleImportValidationRowResult,
} from '../types';
import { isRowValid, type ImportedVehicleRow } from './parseImportExcel';
import { deriveStatus, type ImportRowStatus } from './importStatus';
import { runPool } from '../common/utils';
import { useApiClient } from 'src/hooks/useApiClient';

export interface ImportValidationOptions {
	batchSize?: number;
	concurrency?: number;
}

export interface ValidationProgress {
	total: number;
	done: number;
	error: number;
}

export interface FailedValidationRow {
	rowId: string;
	error: string;
}

const DEFAULT_BATCH_SIZE = 1;
const DEFAULT_CONCURRENCY = 6;

const chunk = <T>(items: T[], size: number): T[][] => {
	if (size <= 0 || size >= items.length) return items.length ? [items] : [];
	const batches: T[][] = [];
	for (let i = 0; i < items.length; i += size) {
		batches.push(items.slice(i, i + size));
	}
	return batches;
};

const errorMessage = (reason: unknown): string =>
	(reason as { message?: string })?.message || 'Validazione fallita';

const mergeRowResults = (
	prev: MotornetVehicleImportValidationRowResult[] | undefined,
	incoming: MotornetVehicleImportValidationRowResult[]
): MotornetVehicleImportValidationRowResult[] => {
	const byId = new Map(
		(prev ?? []).map((result) => [result.rowId, result] as const)
	);
	incoming.forEach((result) => byId.set(result.rowId, result));
	return Array.from(byId.values());
};

export const useImportValidation = (
	rows: ImportedVehicleRow[],
	options: ImportValidationOptions = {}
) => {
	const { batchSize = DEFAULT_BATCH_SIZE, concurrency = DEFAULT_CONCURRENCY } =
		options;
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();

	const [progress, setProgress] = useState<ValidationProgress>({
		total: 0,
		done: 0,
		error: 0,
	});
	const [errorByRowId, setErrorByRowId] = useState<Record<string, string>>({});
	const [isRetrying, setIsRetrying] = useState(false);

	const requestItems = useMemo<MotornetVehicleImportValidateRequestItem[]>(
		() =>
			rows
				.filter((row) => isRowValid(row))
				.map((row) => ({
					rowId: row.rowId,
					manufactureCode: row.codice_costruttore,
					optionsInternalCodes: row.codice_colore_interno || undefined,
					optionsExternalCodes: row.codice_colore_esterno || undefined,
					optionsCodes: row.optional_list,
				})),
		[rows]
	);

	const queryKey = useMemo(
		() => [
			'motornet',
			'import-validate',
			batchSize,
			requestItems.map((item) => item.rowId),
		],
		[batchSize, requestItems]
	);

	const runBatches = useCallback(
		async (
			items: MotornetVehicleImportValidateRequestItem[],
			onFulfilled: (
				batch: MotornetVehicleImportValidateRequestItem[],
				results: MotornetVehicleImportValidationRowResult[]
			) => void
		) => {
			setProgress({ total: items.length, done: 0, error: 0 });
			await runPool(
				chunk(items, batchSize),
				(batch) =>
					apiClient.motornetControllerService.validateImportRows(batch),
				{
					concurrency,
					onSettle: (batch, result: any) => {
						if (result.status === 'fulfilled') {
							onFulfilled(batch, result.value);
							setErrorByRowId((prev) => {
								const next = { ...prev };
								batch.forEach((item) => delete next[item.rowId]);
								return next;
							});
						} else {
							const message = errorMessage(result.reason);
							setErrorByRowId((prev) => {
								const next = { ...prev };
								batch.forEach((item) => {
									next[item.rowId] = message;
								});
								return next;
							});
						}
						setProgress((prev) => ({
							...prev,
							done: prev.done + batch.length,
							error:
								result.status === 'rejected'
									? prev.error + batch.length
									: prev.error,
						}));
					},
				}
			);
		},
		[apiClient, batchSize, concurrency]
	);

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			setErrorByRowId({});
			const merged: MotornetVehicleImportValidationRowResult[] = [];
			await runBatches(requestItems, (_batch, results) => {
				merged.push(...results);
			});
			return merged;
		},
		enabled: requestItems.length > 0,
		retry: false,
		refetchOnWindowFocus: false,
	});

	const retryFailed = useCallback(async () => {
		const failedIds = new Set(Object.keys(errorByRowId));
		const retryItems = requestItems.filter((item) => failedIds.has(item.rowId));
		if (retryItems.length === 0) return;

		setIsRetrying(true);
		try {
			await runBatches(retryItems, (_batch, results) => {
				queryClient.setQueryData<MotornetVehicleImportValidationRowResult[]>(
					queryKey,
					(prev) => mergeRowResults(prev, results)
				);
			});
		} finally {
			setIsRetrying(false);
		}
	}, [errorByRowId, requestItems, runBatches, queryClient, queryKey]);

	useEffect(() => {
		if (requestItems.length === 0) {
			setErrorByRowId({});
			setProgress({ total: 0, done: 0, error: 0 });
		}
	}, [requestItems.length]);

	const resultByRowId = useMemo(() => {
		const map = new Map<string, MotornetVehicleImportValidationRowResult>();
		(data ?? []).forEach((result) => map.set(result.rowId, result));
		return map;
	}, [data]);

	const statusForRow = useCallback(
		(row: ImportedVehicleRow): ImportRowStatus =>
			deriveStatus(row, resultByRowId.get(row.rowId), isLoading || isRetrying),
		[resultByRowId, isLoading, isRetrying]
	);

	const matchesForRow = useCallback(
		(row: ImportedVehicleRow): MotornetVehicleImportValidationMatch[] =>
			resultByRowId.get(row.rowId)?.matches ?? [],
		[resultByRowId]
	);

	const errorForRow = useCallback(
		(row: ImportedVehicleRow): string | undefined => errorByRowId[row.rowId],
		[errorByRowId]
	);

	const failedRows = useMemo<FailedValidationRow[]>(
		() =>
			Object.entries(errorByRowId).map(([rowId, error]) => ({ rowId, error })),
		[errorByRowId]
	);

	return {
		statusForRow,
		matchesForRow,
		errorForRow,
		isChecking: isLoading,
		isRetrying,
		progress,
		failedRows,
		retryFailed,
	};
};
